import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { tokens } from '../tokens';
import BottomNav, { NavGlyph, NAV_ITEMS } from './BottomNav';

function useIsDesktop(breakpoint = 900) {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(min-width: ${breakpoint}px)`).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const handler = (e) => setIsDesktop(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [breakpoint]);
  return isDesktop;
}

export default function Navbar({ hideTopBar = false }) {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const location = useLocation();
  const isDesktop = useIsDesktop();

  const isMod = user.role === 'moderator' || user.role === 'admin';
  const isAdmin = user.role === 'admin';

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Desktop top nav with centered links
  if (isDesktop) {
    return (
      <div style={{
        position: 'relative', zIndex: 20,
        borderBottom: `1px solid ${tokens.edge}`,
        background: `${tokens.ground}f2`,
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 36px', gap: 40,
        }}>
          {/* Left: wordmark */}
          <Link to="/" style={{ textDecoration: 'none' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 9,
              fontFamily: tokens.fontMono, fontSize: 11,
              letterSpacing: '0.22em', textTransform: 'uppercase',
              color: tokens.accent, fontWeight: 500,
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: '50%', background: tokens.accent,
                boxShadow: `0 0 10px ${tokens.accent}`,
                animation: 'gl-breathe 3.5s ease-in-out infinite',
              }} />
              ghostlight
            </div>
          </Link>

          {/* Center: nav links */}
          <nav style={{ display: 'flex', gap: 2, flex: 1, justifyContent: 'center' }}>
            {NAV_ITEMS.map(item => {
              const isActive = item.path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.path);
              return (
                <Link key={item.id} to={item.path} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 16px',
                  color: isActive ? tokens.accent : tokens.textMid,
                  borderBottom: isActive ? `1px solid ${tokens.accent}` : '1px solid transparent',
                  transition: `color 180ms ${tokens.ease}`,
                  textDecoration: 'none',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = tokens.textHi; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = tokens.textMid; }}
                >
                  <NavGlyph kind={item.glyph} size={16}
                    color={isActive ? tokens.accent : 'currentColor'}
                    glow={isActive ? tokens.accent : null} />
                  <span style={{
                    fontFamily: tokens.fontBody, fontSize: 13,
                    letterSpacing: '0.02em',
                  }}>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Right: user dropdown */}
          <div ref={menuRef} style={{ position: 'relative', minWidth: 140, display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                borderRadius: 8,
                transition: `background 260ms ${tokens.ease}`,
              }}
              onMouseEnter={e => e.currentTarget.style.background = `${tokens.edge}`}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <div style={{ textAlign: 'right' }}>
                <div style={{
                  fontFamily: tokens.fontDisplay,
                  fontSize: 13,
                  color: tokens.accentDim,
                  lineHeight: 1.2,
                  fontWeight: tokens.displayWeight,
                }}>{user.alias}</div>
                <div style={{
                  fontFamily: tokens.fontBody,
                  fontSize: 11,
                  color: tokens.textLow,
                  lineHeight: 1.2,
                }}>{user.department}</div>
              </div>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                stroke={tokens.textLow} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ transition: 'transform 0.2s ease', transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
              >
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>

            {menuOpen && (
              <div style={{
                position: 'absolute', right: 0, top: '100%', marginTop: 6,
                background: tokens.house,
                border: `1px solid ${tokens.edge}`,
                borderRadius: 10, padding: '4px 0', minWidth: 156,
                boxShadow: '0 12px 32px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.2)',
                zIndex: 100,
              }}>
                {isMod && (
                  <Link to="/mod" onClick={() => setMenuOpen(false)} style={dropdownItemStyle}
                    onMouseEnter={e => e.currentTarget.style.background = tokens.edge}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  >Moderation</Link>
                )}
                {isAdmin && (
                  <Link to="/admin" onClick={() => setMenuOpen(false)} style={dropdownItemStyle}
                    onMouseEnter={e => e.currentTarget.style.background = tokens.edge}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  >Admin</Link>
                )}
                {(isMod || isAdmin) && (
                  <div style={{ height: 1, background: tokens.edge, margin: '4px 12px' }} />
                )}
                <Link to="/settings" onClick={() => setMenuOpen(false)} style={dropdownItemStyle}
                  onMouseEnter={e => e.currentTarget.style.background = tokens.edge}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >Settings</Link>
                <div style={{ height: 1, background: tokens.edge, margin: '4px 12px' }} />
                <button onClick={() => { setMenuOpen(false); logout(); }}
                  style={{
                    ...dropdownItemStyle, width: '100%', textAlign: 'left',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: tokens.textLow,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = tokens.edge}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Mobile layout: top bar + bottom nav
  return (
    <>
      {/* ── Top Bar (hidden on pages with custom headers like Home) ── */}
      {!hideTopBar && (
        <nav style={{
          background: tokens.ground,
          borderBottom: `1px solid ${tokens.edge}`,
        }}>
          <div style={{ maxWidth: 960, margin: '0 auto', padding: '0 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 52 }}>
              <Link to="/" style={{ textDecoration: 'none' }}>
                <span style={{
                  fontFamily: tokens.fontMono, fontSize: 11, letterSpacing: '0.3em',
                  textTransform: 'uppercase', color: tokens.accent, fontWeight: 500,
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%', background: tokens.accent,
                    boxShadow: `0 0 8px ${tokens.accent}`, animation: 'gl-breathe 3.5s ease-in-out infinite',
                  }} />
                  ghostlight
                </span>
              </Link>

              {/* User dropdown */}
              <div ref={menuRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                    borderRadius: 8,
                    transition: `background 260ms ${tokens.ease}`,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = `${tokens.edge}`}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontFamily: tokens.fontDisplay,
                      fontSize: 13,
                      color: tokens.accentDim,
                      lineHeight: 1.2,
                      fontWeight: tokens.displayWeight,
                    }}>{user.alias}</div>
                    <div style={{
                      fontFamily: tokens.fontBody,
                      fontSize: 11,
                      color: tokens.textLow,
                      lineHeight: 1.2,
                    }}>{user.department}</div>
                  </div>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke={tokens.textLow} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ transition: 'transform 0.2s ease', transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  >
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>

                {menuOpen && (
                  <div style={{
                    position: 'absolute', right: 0, top: '100%', marginTop: 6,
                    background: tokens.house,
                    border: `1px solid ${tokens.edge}`,
                    borderRadius: 10, padding: '4px 0', minWidth: 156,
                    boxShadow: '0 12px 32px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.2)',
                    zIndex: 100,
                  }}>
                    <Link to="/settings" onClick={() => setMenuOpen(false)} style={dropdownItemStyle}
                      onMouseEnter={e => e.currentTarget.style.background = tokens.edge}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    >Settings</Link>
                    <div style={{ height: 1, background: tokens.edge, margin: '4px 12px' }} />
                    <button onClick={() => { setMenuOpen(false); logout(); }}
                      style={{
                        ...dropdownItemStyle, width: '100%', textAlign: 'left',
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: tokens.textLow,
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = tokens.edge}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </nav>
      )}

      {/* ── Bottom Navigation ── */}
      <BottomNav tokens={tokens} isMod={isMod} isAdmin={isAdmin} />
    </>
  );
}

const dropdownItemStyle = {
  display: 'block', padding: '9px 16px',
  fontFamily: '"Geist", "Inter", system-ui, sans-serif',
  fontSize: 13,
  color: 'rgba(237,228,211,0.7)', textDecoration: 'none',
  transition: 'background 0.15s ease',
  borderRadius: 0,
};
